import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // Dodano
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const CreateTableModal = ({ isOpen, onClose, idAzienda }) => {
  const { t } = useTranslation(); // Hook za prevod
  const [tableName, setTableName] = useState('');
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([{ name: '', type: 'text', isDropdown: false }]);

  if (!isOpen) return null;

  const addColumn = () => setColumns([...columns, { name: '', type: 'text', isDropdown: false }]);
  const removeColumn = (index) => columns.length > 1 && setColumns(columns.filter((_, i) => i !== index));
  const updateColumn = (index, field, value) => {
    const newCols = [...columns];
    newCols[index][field] = value;
    setColumns(newCols);
  };

  const handleCreate = async () => {
    if (!tableName.trim()) return alert(t('create_table_modal.alert_empty_name'));
    setLoading(true);
    
    try {
      // 1. Formatiranje imena tabele
      const cleanTableName = `inv_${tableName.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
      
      // 2. Mapiranje tipova za PostgreSQL (Supabase)
      const typeMap = { 
        text: 'TEXT', 
        number: 'NUMERIC', 
        date: 'DATE', 
        boolean: 'BOOLEAN' 
      };

      const settingsJson = {};
      
      const columnsSql = columns
        .filter(col => col.name.trim() !== '')
        .map(col => {
          const cleanColName = col.name.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          
          // Čuvanje metapodataka o dropdown-u
          if (col.isDropdown) settingsJson[cleanColName] = { type: 'dropdown' };
          
          // Definicija kolone za SQL rpc
          return `${cleanColName} ${typeMap[col.type]}`;
        }).join(', ');

      // 3. RPC Poziv ka Supabase bazi
      const { error } = await supabase.rpc('create_inventory_table', {
        p_table_name: cleanTableName,
        p_columns: columnsSql,
        p_id_azienda: idAzienda,
        p_settings: settingsJson
      });

      if (error) throw error;
      
      // Uspješno kreirano
      window.location.reload();
    } catch (err) {
      console.error("Errore durante la creazione della tabella:", err);
      alert(t('create_table_modal.alert_error') + err.message);
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-background/90 backdrop-blur-xl animate-in fade-in duration-300">
      <div className="bg-card w-full max-w-2xl rounded-[3rem] border border-border shadow-2xl overflow-hidden animate-in zoom-in-95">
        
        <div className="p-10 border-b border-border/50 flex justify-between items-center bg-muted/20">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tighter">{t('create_table_modal.title')}</h2>
            <p className="text-[10px] text-primary font-black uppercase tracking-widest mt-1">{t('create_table_modal.system_info')}</p>
          </div>
          <button onClick={onClose} className="p-4 hover:bg-muted rounded-2xl transition-all"><Icon name="X" size={24} /></button>
        </div>

        <div className="p-10 max-h-[60vh] overflow-y-auto custom-scrollbar space-y-8">
          <div className="space-y-3">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('create_table_modal.label_table_name')}</label>
            <div className="relative">
              <div className="absolute left-5 top-1/2 -translate-y-1/2 text-primary/40 font-black text-xs tracking-widest">INV_</div>
              <input 
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                className="w-full bg-background border border-border/60 rounded-2xl py-4 pl-14 pr-5 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                placeholder={t('create_table_modal.placeholder_table_name')}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('create_table_modal.label_columns')}</label>
              <button onClick={addColumn} className="p-2 text-primary hover:bg-primary/10 rounded-xl transition-all"><Icon name="Plus" size={20} /></button>
            </div>

            {columns.map((col, index) => (
              <div key={index} className="p-6 bg-muted/20 rounded-[2rem] border border-border/40 space-y-4">
                <div className="flex gap-3">
                  <input 
                    type="text"
                    placeholder={t('create_table_modal.placeholder_col_name')}
                    value={col.name}
                    onChange={(e) => updateColumn(index, 'name', e.target.value)}
                    className="flex-1 bg-background border border-border/60 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                  />
                  <select 
                    value={col.type}
                    onChange={(e) => updateColumn(index, 'type', e.target.value)}
                    className="bg-background border border-border/60 rounded-xl px-3 py-3 text-[10px] font-black uppercase tracking-widest outline-none focus:ring-4 focus:ring-primary/10"
                  >
                    <option value="text">{t('create_table_modal.types.text')}</option>
                    <option value="number">{t('create_table_modal.types.number')}</option>
                    <option value="date">{t('create_table_modal.types.date')}</option>
                    <option value="boolean">{t('create_table_modal.types.boolean')}</option>
                  </select>
                  <button onClick={() => removeColumn(index)} className="p-3 text-muted-foreground hover:text-destructive transition-colors"><Icon name="Trash2" size={18} /></button>
                </div>
                <div className="flex items-center gap-3 px-1">
                  <input 
                    type="checkbox" 
                    id={`drop-new-${index}`} 
                    checked={col.isDropdown}
                    onChange={(e) => updateColumn(index, 'isDropdown', e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20 cursor-pointer"
                  />
                  <label htmlFor={`drop-new-${index}`} className="text-[9px] font-black uppercase text-muted-foreground cursor-pointer select-none">{t('create_table_modal.label_dropdown')}</label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-10 bg-muted/10 border-t border-border/50 flex gap-4">
          <button onClick={onClose} className="flex-1 py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-muted transition-all">{t('create_table_modal.btn_cancel')}</button>
          <button onClick={handleCreate} disabled={loading} className="flex-[2] py-5 bg-foreground text-background rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-primary hover:text-white transition-all disabled:opacity-50">
            {loading ? t('create_table_modal.btn_creating') : t('create_table_modal.btn_create')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTableModal;